import React, { useState, useEffect } from 'react';
import {
  Center,
  ChakraProvider,
  Container,
  Flex,
  Heading,
  theme,
  Stack,
  useMediaQuery,
  Button,
  VStack,
  Box,
  Divider
} from '@chakra-ui/react';
import { nanoid } from 'nanoid';
import Question from './components/Question';
import MobileQuestion from './components/MobileQuestion';
import ReplaceButton from './components/ReplaceButton';
import Header from './components/Header';
import useLocalStorage from './hooks/useLocalStorage';
import { CATEGORIES, RESULT_EMOJIS } from './utils/constants';
import { isNewQuizAvailable } from './utils';
import Result from './components/Result';

function App() {
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const [quizOver, setQuizOver] = useState(false);
  const [quizData, setQuizData] = useLocalStorage('dtp-quiz', [])
  const [allQSeen, setAllQSeen] = useState(false);
  const [sessionData, setSessionData] = useLocalStorage('dtp-session', {
    firstRun: true,
    lastFinishedTime: 0,
    lastPlayedTime: 0,
    lastPlayedDay: '',
    isFinished: false,
    isRestoredSession: false,
    swapUsed: false,
    score: -1
  })
  const [isLoading, setIsLoading] = useState(false);
  const [swapUsed, setSwapUsed] = useState(false);
  const [resultText, setResultText] = useState([]);
  const [userStats, setUserStats] = useLocalStorage('dtp-stats', {
    gamesPlayed:0,
    averagePerformance:0,
    categoryPerformance: CATEGORIES.reduce((a, v) => ({ ...a, [v]: 0}), {}) 
  })
  useEffect(() => {
    if(swapUsed) {
      setSessionData({...sessionData, swapUsed: true})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapUsed]);
  useEffect(() => {
    // Check if new Day has begun in UTC time, get new daily quiz
    if(isNewQuizAvailable(sessionData.lastPlayedDay)) {
      console.log("Fetching new quiz")
      setIsLoading(true)
      try{
        fetch("https://b8s2lw6ys9.execute-api.ap-south-1.amazonaws.com/dtp/quiz")
      .then(res =>res.json())
      .then(data => {
        setSessionData({...sessionData, isRestoredSession: false, isFinished: false, score: -1, swapUsed:false, lastPlayedDay: data.quizId, lastPlayedTime: Date.now()})
        setQuizData(data.quiz.results.map(item => {
          return {...item, selected:"", id:nanoid(), isCorrect:false}
      }))})
      .then(() =>shuffleAnswers())
      .then(() => setIsLoading(false))
      }
      catch(err) {
        console.log(err);
      }
      
    }
    else if(sessionData.isFinished) { 
      setSessionData({...sessionData, isRestoredSession:true})
      setSwapUsed(sessionData.swapUsed)
      setQuizOver(true) } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function firstRunModalShown() {
    setSessionData({...sessionData, firstRun:false})
  }
  function updateSelection(qid, text) {
    setQuizData(prevQuizData => prevQuizData.map(question => {
        return question.id === qid ?
            {...question, selected:text} :
            question
    }))
  }

  function replaceQuestion(id) {
    let index = quizData.findIndex((data) => data.id === id)
    let newQuizData = quizData;
    newQuizData[index] = quizData[5]
    newQuizData = newQuizData.slice(0, newQuizData.length-1)
    setQuizData(newQuizData)
    setSwapUsed(true)
    
  }
  function shuffleAnswers() {
    //console.log(quizData)
    setQuizData(prevQuizData => prevQuizData.map(question => {
        const unshuffled = question.incorrect_answers.concat(question.correct_answer)
        let answers = unshuffled
        .map(value => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value)
        return {...question, answers:answers}
    }))
  }
  function generateResultText() {
    let resText = quizData.map((question, index) => {
    return (
      question.selected ? 
      question.isCorrect ? RESULT_EMOJIS.correct : RESULT_EMOJIS.incorrect :
      RESULT_EMOJIS.unanswered)
    })
    if (!swapUsed) { // BUG? Save swap used to session data (repro?)
      resText.push(RESULT_EMOJIS.noswap)
    }
    setResultText(resText)
  }
  function updateStatsForSession() {
    if(!sessionData.isRestoredSession){
      let tempUserStats = userStats
      quizData.forEach(question => {
        if(question.isCorrect) {
          tempUserStats.categoryPerformance[question.category] += 1
        }
      });
      tempUserStats.gamesPlayed += 1;
      tempUserStats.averagePerformance = ((tempUserStats.averagePerformance*(tempUserStats.gamesPlayed-1)) + (sessionData.score || 0))/ tempUserStats.gamesPlayed;
      setUserStats({...userStats, ...tempUserStats})
    }

  }
  useEffect(() => {
    if(quizOver){
      generateResultText()
      updateStatsForSession()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizOver]);
  function checkAnswers() {
    setQuizData(prevQuizData => prevQuizData.reduce((memo, question, index) => {
      if((!swapUsed && index < prevQuizData.length-1) || swapUsed) { // Swap unused, discard extra question
        memo.push(question.selected === question.correct_answer ? 
          {...question, isCorrect:true} : question)
      }
      return memo;
    },[]))
      const result = quizData.filter(question => question.selected === question.correct_answer ).length
      setSessionData({...sessionData, score: result, lastFinishedTime:Date.now(), isFinished:true})
      setQuizOver(true)
  }
  return (
    <ChakraProvider theme={theme}>
      <Container h={'100vh'} maxW={'100vw'} >
        {isLoading ? 
        <Center h={'100%'}>
          <Heading size={'md'} fontWeight='thin' fontStyle={'italic'}>Reticulating splines...</Heading>
        </Center>
        :
        <Flex className='test' pt={5} align={'center'} flexDir={'column'} h='100%'>
          <Header isFirstRun={sessionData.firstRun} onClosePopOver={firstRunModalShown} stats={userStats}/>
          <Center className='centerContainer' h={'100%'} w={{base:'100%', md:'70%'}} >
            <VStack spacing={2} maxW={'100%'}>
              {!isMobile ? 
                <Stack py={5} maxH={'100%'}>
                {quizData.map((question, index) => {
                  if (index >= 5) return(<></>);
                  return(
                    <Box key={question.id} shadow='md'>
                      {!swapUsed && !quizOver && <ReplaceButton key={index} replaceQuestion={()=>replaceQuestion(question.id)}/>}
                      <Question
                        
                        question={question} 
                        updateSelection={updateSelection} 
                        quizOver={quizOver}
                        swapUsed={swapUsed}
                      />
                    </Box>
                )})}
              </Stack>
              :
              <MobileQuestion 
                quizData={quizData}
                updateSelection={updateSelection} 
                replaceQuestion={replaceQuestion}
                quizOver={quizOver}
                swapUsed={swapUsed}
                setAllQSeen={setAllQSeen}
              />
              }
              <Divider />
              <Box pt={10}>
              {quizOver ? 
                <Result id={sessionData.lastPlayedDay} score={sessionData.score} resultText={resultText}/>
              : 
                isMobile ?
                  allQSeen && <Button colorScheme={'cyan'} rounded={'full'} onClick={checkAnswers}>Check Result</Button>:
                  <Button colorScheme={'cyan'} rounded={'full'} onClick={checkAnswers}>Check Result</Button>
              }
              </Box>
            </VStack>
                   
          </Center>
        </Flex>}
      </Container>
    </ChakraProvider>
  );
}

export default App;
